import { apiClient, kirbyApiClient } from '@solid/shared/services/api.service';
import { getLocalStorageKey, setLocalStorageItem } from './local-storage.service';
import { KirbyCheckGroup, KirbyInfoGeneral, KirbyInfoPlus, KirbyCheck, KirbyDemoContent } from '@solid/shared/kirby';
import { LOCAL_STORAGE_LANGUAGE_KEY } from '../i18n/i18n';

export enum KIRBY_CACHE_KEY {
  CHECK_GROUPS = 'CHECK_GROUPS',
  INFO_GENERAL = 'INFO_GENERAL',
  INFO_PLUS = 'INFO_PLUS',
  CHECKS = 'CHECKS',
  DEMO_CONTENT = 'DEMO_CONTENT1',
}

type KirbyData = KirbyCheckGroup[] | KirbyInfoGeneral | KirbyInfoPlus | KirbyCheck[] | KirbyDemoContent;

/**
 * Local cache that persist the latest data and can be used to prevent site flashing, as this API's content rarely changes.
 */
const getCacheKey = (cacheKey: KIRBY_CACHE_KEY) => `_KIRBY_DATA__${cacheKey}`;

export function getKirbyPlaceholderDataFromCache(cacheKey: KIRBY_CACHE_KEY): KirbyData {
  const entry = getLocalStorageKey(getCacheKey(cacheKey));
  if (entry) {
    try {
      return JSON.parse(entry);
    } catch {
      return undefined;
    }
  }
}
export async function getKirbyChecks(): Promise<KirbyCheck[]> {
  const language = getLocalStorageKey(LOCAL_STORAGE_LANGUAGE_KEY);
  const res = await apiClient.get<{ data: KirbyCheck[] }>('/v1/checks/info', {
    params: {
      language,
    },
  });
  const { data } = res.data;
  setLocalStorageItem(getCacheKey(KIRBY_CACHE_KEY.CHECKS), JSON.stringify(data));
  return data;
}

export async function getKirbyInfoPlus(): Promise<KirbyInfoPlus> {
  const language = getLocalStorageKey(LOCAL_STORAGE_LANGUAGE_KEY);
  const res = await kirbyApiClient.get<{ data: KirbyInfoPlus }>('/api/solid/info/plus', {
    params: {
      language,
    },
  });
  const { data } = res.data;
  setLocalStorageItem(getCacheKey(KIRBY_CACHE_KEY.INFO_PLUS), JSON.stringify(data));
  return data;
}

export async function getKirbyInfoGeneral(): Promise<KirbyInfoGeneral> {
  const language = getLocalStorageKey(LOCAL_STORAGE_LANGUAGE_KEY);
  const res = await kirbyApiClient.get<{ data: KirbyInfoGeneral }>('/api/solid/info/general', {
    params: {
      language,
    },
  });
  const { data } = res.data;
  setLocalStorageItem(getCacheKey(KIRBY_CACHE_KEY.INFO_GENERAL), JSON.stringify(data));
  return data;
}

export async function getKirbyDemoContent(): Promise<KirbyDemoContent> {
  const language = getLocalStorageKey(LOCAL_STORAGE_LANGUAGE_KEY);
  const res = await apiClient.get<{ data: KirbyDemoContent }>('/v1/data/demo-content', {
    params: {
      language,
    },
  });
  const { data } = res.data;
  setLocalStorageItem(getCacheKey(KIRBY_CACHE_KEY.DEMO_CONTENT), JSON.stringify(data));
  return data;
}

export async function getKirbyCheckGroups(): Promise<KirbyCheckGroup[]> {
  const language = getLocalStorageKey(LOCAL_STORAGE_LANGUAGE_KEY);
  const res = await kirbyApiClient.get<{ data: KirbyCheckGroup[] }>('/api/solid/check-groups', {
    params: {
      language,
    },
  });
  const { data } = res.data;
  setLocalStorageItem(getCacheKey(KIRBY_CACHE_KEY.CHECK_GROUPS), JSON.stringify(data));
  return data;
}
